/**
 * src/components/public/layout/LocaleSwitcher.tsx
 *
 * Dil değiştirici. 4 dile kadar inline toggle, 5+ dropdown.
 *
 * `enabledLocales` prop (opsiyonel): Server Component'ten gelen runtime-aktif
 * locale code listesi. SUPPORTED_LOCALES build-time 15 dil içerir (routing
 * için); switcher sadece admin'in **aktif** ettiği dilleri göstermeli.
 *
 * Client Component cunku useRouter + usePathname.
 */
'use client'

import { ChevronDown, Globe2 } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState, useTransition } from 'react'

import { usePathname, useRouter } from '@/lib/i18n/navigation'
import { trackEvent } from '@/lib/analytics/gtag'
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type Locale,
} from '@/lib/i18n/config'

/**
 * 2 dile kadar inline toggle ("TR | EN"); 3+ dropdown'a geç.
 *
 * Tasarım kararı: 3 dil "TR | EN | DE" inline gözükebilir ama topbar yatay
 * alan dar (mobile + telefon/email/CTA ile birlikte); dropdown daha temiz +
 * scale-out hazır (10 dil aktif olsa bile UI bozulmaz).
 */
const INLINE_LIMIT = 2

interface LocaleSwitcherProps {
  /**
   * Runtime'da enabled locale code listesi. Verilmezse SUPPORTED_LOCALES (15)
   * fallback — backwards compat.
   */
  enabledLocales?: readonly string[]
}

export function LocaleSwitcher({ enabledLocales }: LocaleSwitcherProps = {}) {
  const currentLocale = useLocale() as Locale
  const t = useTranslations('a11y')
  const router = useRouter()
  const pathname = usePathname()
  const [isPending, startTransition] = useTransition()

  // Görüntülenen locale listesi: prop verildiyse runtime filtre, yoksa
  // SUPPORTED_LOCALES (eski davranış). SUPPORTED_LOCALES'in 15 dili routing
  // için zorunlu (build-time const); switcher sadece aktif dilleri göstermeli.
  const visibleLocales = useMemo<readonly Locale[]>(() => {
    if (!enabledLocales || enabledLocales.length === 0) return SUPPORTED_LOCALES
    return SUPPORTED_LOCALES.filter((l) => enabledLocales.includes(l))
  }, [enabledLocales])

  /**
   * Yeni locale'a gec; current path'i koruyarak yonlendir.
   *
   * Önce DOM'daki `<link rel="alternate" hreflang="X" href="...">` tag'ine bak
   * (her sayfa generateMetadata'da alternates.languages set eder — DB-driven,
   * doğru slug). Varsa direkt o URL'e git. Yoksa next-intl pathnames mapping
   * fallback (statik sayfa map'ten URL üretir).
   */
  function switchTo(target: Locale) {
    if (target === currentLocale || isPending) return

    // Analitik: dil değişimi (onay varsa GA'ya gider; yoksa no-op).
    trackEvent('locale_switch', { from: currentLocale, to: target })

    // SSR safety — typeof window check
    if (typeof window !== 'undefined') {
      const alternateLink = document.querySelector<HTMLLinkElement>(
        `link[rel="alternate"][hreflang="${target}"]`,
      )
      const alternateUrl = alternateLink?.href
      if (alternateUrl) {
        startTransition(() => {
          window.location.href = alternateUrl
        })
        return
      }
    }

    // Fallback: next-intl pathnames mapping
    startTransition(() => {
      router.replace(pathname, { locale: target })
    })
  }

  // 4 dile kadar inline; üzeri dropdown.
  if (visibleLocales.length <= INLINE_LIMIT) {
    return (
      <div
        className="inline-flex items-center text-xs font-medium tracking-wider"
        role="group"
        aria-label={t('locale_switcher')}
      >
        {visibleLocales.map((loc, idx) => {
          const isActive = loc === currentLocale
          return (
            <span key={loc} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => switchTo(loc)}
                disabled={isActive || isPending}
                className={
                  // text-brand-100 on bg-brand-950 = ~7:1 contrast (WCAG 2.1 AAA bile karsilar).
                  // Onceki text-brand-200/70 = 4.12:1 (AA 4.5:1 altinda — axe violation).
                  'inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-2 ' +
                  (isActive
                    ? 'text-white font-semibold cursor-default'
                    : 'text-brand-100 hover:text-white transition-colors cursor-pointer')
                }
                {...(isActive && { 'aria-current': 'true' as const })}
                aria-label={LOCALE_LABELS[loc] ?? loc.toUpperCase()}
              >
                {loc.toUpperCase()}
              </button>
              {idx < visibleLocales.length - 1 && (
                // aria-hidden ile a11y zorunlu degil ama gorsel separator
                // /50 alpha = ~3:1 contrast (UI element AA esigi karsilar)
                <span className="mx-1.5 text-brand-200/50" aria-hidden="true">
                  /
                </span>
              )}
            </span>
          )
        })}
      </div>
    )
  }

  // 5+ dil — dropdown.
  return (
    <LocaleDropdown
      current={currentLocale}
      visibleLocales={visibleLocales}
      switchTo={switchTo}
      isPending={isPending}
      label="Dil seçici"
    />
  )
}

/** Dropdown variant — 5+ locale için. Outside-click ile kapanır. */
function LocaleDropdown({
  current,
  visibleLocales,
  switchTo,
  isPending,
  label,
}: {
  current: Locale
  visibleLocales: readonly Locale[]
  switchTo: (loc: Locale) => void
  isPending: boolean
  label: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  /** Trigger buton — Escape ile kapanışta fokusu buraya geri döndürmek için. */
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    // Escape ile kapatma — dropdown yalnızca dış tıklamayı dinliyordu; klavye
    // kullanıcısı menüyü kapatmanın standart yolundan mahrumdu (WCAG 2.1.1).
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', handle)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handle)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        disabled={isPending}
        aria-label={label}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-xs font-medium tracking-wider text-white hover:bg-white/10 rounded-md transition-colors disabled:opacity-50"
      >
        <Globe2 size={14} aria-hidden="true" />
        {current.toUpperCase()}
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 z-50 min-w-[200px] max-h-80 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl py-1"
        >
          {visibleLocales.map((loc) => {
            const isActive = loc === current
            return (
              <button
                key={loc}
                type="button"
                onClick={() => {
                  switchTo(loc)
                  setOpen(false)
                }}
                disabled={isActive || isPending}
                aria-current={isActive ? 'true' : undefined}
                className={
                  'block w-full text-left px-3 py-2 text-sm ' +
                  (isActive
                    ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-medium cursor-default'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800')
                }
              >
                <span className="font-medium">{LOCALE_LABELS[loc] ?? loc.toUpperCase()}</span>
                <span className="text-slate-400 dark:text-slate-500 ml-2 text-xs uppercase">
                  {loc}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
